import React, { useEffect, useState } from "react";
import { Star, MessageSquare, BadgeCheck, Loader2, Send, CornerDownRight, Store } from "lucide-react";
import api from "../../api";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../hooks/use-toast";
import { useLang } from "../../context/LanguageContext";

const StarRow = ({ value, onChange, size = 18, testIdPrefix }) => (
  <div className="flex items-center gap-1">
    {[1, 2, 3, 4, 5].map((s) => (
      <button
        key={s}
        type={onChange ? "button" : undefined}
        onClick={onChange ? () => onChange(s) : undefined}
        disabled={!onChange}
        data-testid={onChange && testIdPrefix ? `${testIdPrefix}-${s}` : undefined}
        className={onChange ? "transition-transform hover:scale-110 cursor-pointer" : "cursor-default"}
        aria-label={`${s} star${s === 1 ? "" : "s"}`}
      >
        <Star size={size} className={s <= value ? "fill-[#f5b942] text-[#f5b942]" : "text-muted-foreground/40"} />
      </button>
    ))}
  </div>
);

export const BusinessReviews = ({ name }) => {
  const { t } = useLang();
  const { user } = useAuth();
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [replyOpen, setReplyOpen] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replying, setReplying] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/business/${encodeURIComponent(name)}/reviews`);
      setData(data);
      if (data.myReview) { setRating(data.myReview.rating || 0); setComment(data.myReview.comment || ""); }
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [name]);

  const submit = async () => {
    if (rating < 1) { toast({ title: t("rev.pickAStarRating"), description: t("rev.tapStarsToRate") }); return; }
    setSubmitting(true);
    try {
      const { data: res } = await api.post(`/business/${encodeURIComponent(name)}/reviews`, { rating, comment });
      toast({ title: res.updated ? "Review updated" : "Thanks for your review!", description: t("rev.yourVerifiedReviewIs") });
      await load();
    } catch (e) {
      toast({ title: t("rev.couldnTPostReview"), description: e?.response?.data?.detail || "Please try again." });
    } finally { setSubmitting(false); }
  };

  const openReply = (r) => { setReplyOpen(r.id); setReplyText(r.reply?.text || ""); };

  const sendReply = async (reviewId) => {
    if (!replyText.trim()) { toast({ title: t("rev.replyIsEmpty"), description: t("rev.writeAShortReply") }); return; }
    setReplying(true);
    try {
      await api.post(`/business/${encodeURIComponent(name)}/reviews/${reviewId}/reply`, { text: replyText });
      toast({ title: t("rev.replyPosted"), description: t("rev.shoppersCanNowSee") });
      setReplyOpen(null); setReplyText("");
      await load();
    } catch (e) {
      toast({ title: t("rev.couldnTPostReply"), description: e?.response?.data?.detail || "Please try again." });
    } finally { setReplying(false); }
  };

  if (!data) return null;
  const isShopper = user?.role === "shopper";
  const total = data.count || 0;

  return (
    <div id="reviews" className="bg-card border border-border rounded-2xl p-5 sm:p-6 scroll-mt-24" data-testid="business-reviews">
      <div className="flex items-center gap-2 mb-4">
        <MessageSquare size={18} className="text-[#16a34a]" />
        <h2 className="text-lg font-extrabold text-foreground">{t("rev.verifiedReviews")}</h2>
      </div>

      <div className="flex items-center gap-5 mb-5">
        <div className="text-center shrink-0">
          <div className="text-4xl font-extrabold text-foreground" data-testid="reviews-average">{data.average != null ? data.average : "—"}</div>
          <StarRow value={Math.round(data.average || 0)} size={14} />
          <div className="text-xs text-muted-foreground mt-1" data-testid="reviews-count">{total} review{total === 1 ? "" : "s"}</div>
        </div>
        <div className="flex-1 space-y-1">
          {[5, 4, 3, 2, 1].map((s) => {
            const n = (data.distribution && data.distribution[String(s)]) || 0;
            const pct = total ? Math.round((n / total) * 100) : 0;
            return (
              <div key={s} className="flex items-center gap-2 text-xs">
                <span className="w-3 text-muted-foreground">{s}</span>
                <Star size={11} className="fill-[#f5b942] text-[#f5b942]" />
                <div className="flex-1 h-2 rounded-full bg-secondary overflow-hidden"><div className="h-full bg-[#f5b942]" style={{ width: `${pct}%` }} /></div>
                <span className="w-6 text-right text-muted-foreground">{n}</span>
              </div>
            );
          })}
        </div>
      </div>

      {isShopper && data.canReview && (
        <div className="rounded-xl border border-[#16a34a]/30 bg-[#e7f6ec]/50 dark:bg-[#16341f]/40 p-4 mb-5" data-testid="write-review">
          <p className="text-sm font-bold text-foreground mb-2">{data.myReview ? "Edit your review" : "Rate this business"} <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#16a34a] bg-[#e7f6ec] dark:bg-[#16341f] px-1.5 py-0.5 rounded ml-1"><BadgeCheck size={11} /> {t("rev.verifiedRedemption")}</span></p>
          <StarRow value={rating} onChange={setRating} testIdPrefix="review-star" />
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            data-testid="review-comment"
            rows={3}
            maxLength={1000}
            placeholder={t("rev.shareHowTheDeal")}
            className="mt-3 w-full border border-border rounded-xl px-3 py-2.5 text-sm bg-transparent outline-none focus:border-[#16a34a] text-foreground resize-none"
          />
          <button onClick={submit} disabled={submitting} data-testid="submit-review-btn" className="mt-2 inline-flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white font-bold px-5 py-2.5 rounded-xl transition-colors">
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} {data.myReview ? "Update review" : "Post review"}
          </button>
        </div>
      )}
      {isShopper && !data.canReview && !data.myReview && (
        <p className="text-xs text-muted-foreground mb-5 rounded-xl bg-secondary/60 px-3 py-2.5" data-testid="review-locked-note">{t("rev.redeemOneOfThis")}</p>
      )}

      {data.items.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6" data-testid="no-reviews">{t("rev.noReviewsYetBe")}</p>
      ) : (
        <div className="space-y-4" data-testid="reviews-list">
          {data.items.map((r) => (
            <div key={r.id} className="border-b border-border last:border-0 pb-4 last:pb-0" data-testid={`review-${r.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="font-bold text-foreground text-sm">{r.shopperName}</span>
                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#16a34a] bg-[#e7f6ec] dark:bg-[#16341f] px-1.5 py-0.5 rounded"><BadgeCheck size={11} /> {t("rev.verifiedRedemption")}</span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <StarRow value={r.rating} size={13} />
                <span className="text-xs text-muted-foreground">{r.updatedAt ? String(r.updatedAt).slice(0, 10) : ""}</span>
              </div>
              {r.comment && <p className="text-sm text-foreground mt-2 break-words">{r.comment}</p>}
              {r.dealTitle && <p className="text-xs text-muted-foreground mt-1">On: {r.dealTitle}</p>}

              {r.reply && (
                <div className="mt-3 ml-3 pl-3 border-l-2 border-[#16a34a]/40 bg-secondary/40 rounded-r-lg py-2 pr-2" data-testid={`review-reply-${r.id}`}>
                  <div className="flex items-center gap-1.5 text-xs font-bold text-[#16a34a]"><Store size={12} /> {r.reply.author || name} <span className="text-muted-foreground font-medium">· owner reply</span></div>
                  <p className="text-sm text-foreground mt-1 break-words">{r.reply.text}</p>
                </div>
              )}

              {data.isOwner && (
                replyOpen === r.id ? (
                  <div className="mt-3" data-testid={`reply-form-${r.id}`}>
                    <textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} rows={2} maxLength={500} data-testid={`reply-input-${r.id}`} placeholder={t("rev.replyPubliclyToThis")} className="w-full border border-border rounded-xl px-3 py-2 text-sm bg-transparent outline-none focus:border-[#16a34a] text-foreground resize-none" />
                    <div className="flex items-center gap-2 mt-2">
                      <button onClick={() => sendReply(r.id)} disabled={replying} data-testid={`reply-submit-${r.id}`} className="inline-flex items-center gap-1.5 bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white text-sm font-bold px-4 py-2 rounded-xl transition-colors">{replying ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {r.reply ? "Update reply" : "Post reply"}</button>
                      <button onClick={() => { setReplyOpen(null); setReplyText(""); }} className="text-sm font-semibold text-muted-foreground hover:text-foreground px-2 py-2">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => openReply(r)} data-testid={`reply-btn-${r.id}`} className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-[#16a34a] hover:underline"><CornerDownRight size={13} /> {r.reply ? "Edit reply" : "Reply"}</button>
                )
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BusinessReviews;
